import './numbers.scss';
import { CountUp } from 'countup.js';

/**
 * Numbers counter component.
 *
 * Responsibilities:
 * - Observe the component root and start all counters when it becomes visible
 * - Create and configure CountUp instances with a smooth easing function
 * - Ensure single, idempotent initialization and provide proper cleanup
 *
 * Data attributes on counter elements:
 * - data-numbers-counter-start: number the counter starts from
 * - data-numbers-counter-end: target number to count to (required)
 */
class Numbers {
    /** @param {HTMLElement} $element Root element marked with `[data-numbers="root"]`. */
    constructor ($element) {
        /** @type {HTMLElement|null} */
        this.$root = $element ?? null;
        /** @type {NodeListOf<HTMLElement>} */
        this.$$counters = this.$root?.querySelectorAll('[data-numbers-counter-end]') ?? [];

        /** @type {IntersectionObserver|null} */
        this._observer = null;
        /** @type {boolean} */
        this._started = false;
        /** @type {Array<import('countup.js').CountUp>} */
        this._counters = [];
    }

    /**
     * Initialize the component (idempotent). Sets up the IntersectionObserver.
     * @returns {Numbers} this instance (for chaining)
     */
    initialize () {
        if (!this.$root) return this;
        if (this._observer || this._started) return this; // already set up or finished

        this._observer = new IntersectionObserver((entries) => this.#onIntersect(entries), { threshold: 0 });
        this._observer.observe(this.$root);
        return this;
    }

    /**
     * Destroy the observer and clear references. Safe to call multiple times.
     * @returns {void}
     */
    destroy () {
        this._observer?.unobserve?.(this.$root);
        this._observer?.disconnect?.();
        this._observer = null;
        this._counters = [];
    }

    // ========== private helpers ==========

    /**
     * IntersectionObserver callback. Starts counters once and disconnects observer.
     * @param {IntersectionObserverEntry[]} entries
     * @returns {void}
     */
    #onIntersect (entries) {
        if (this._started) return;
        const visible = entries?.some?.((entry) => entry?.isIntersecting === true) ?? false;
        if (!visible) return;

        this.#buildCounters();
        // start all counters
        this._counters.forEach((cu) => cu?.start?.());
        this._started = true;
        // cleanup observer
        this._observer?.unobserve?.(this.$root);
        this._observer?.disconnect?.();
        this._observer = null;
    }

    /**
     * Build CountUp instances for each counter element.
     * @returns {void}
     */
    #buildCounters () {
        this._counters = [];
        this.$$counters.forEach(($el) => {
            const start = this.#parseIntAttr($el, 'data-numbers-counter-start', 0);
            const end = this.#parseIntAttr($el, 'data-numbers-counter-end', 0);
            this._counters.push(this.#createCounter($el, start, end));
        });
    }

    /**
     * Create a single CountUp instance with easing.
     * @param {HTMLElement} $el Target element (will display the number)
     * @param {number} startValue Start value
     * @param {number} endValue End value
     * @returns {import('countup.js').CountUp}
     */
    #createCounter ($el, startValue, endValue) {
        // cubic easing similar to the legacy implementation
        const easingFn = (t, b, c, d) => {
            const ts = (t /= d) * t;
            const tc = ts * t;
            return b + c * (tc + -3 * ts + 3 * t);
        };

        const options = {
            startVal: Number.isFinite(startValue) ? startValue : 0,
            easingFn,
            separator: ''
        };

        // CountUp accepts an HTMLElement as first argument (prefer over id for robustness)
        return new CountUp($el, Number.isFinite(endValue) ? endValue : 0, options);
    }

    /**
     * Parse integer from attribute with radix 10 and default fallback.
     * @param {HTMLElement} $el
     * @param {string} attr
     * @param {number} [fallback=0]
     * @returns {number}
     */
    #parseIntAttr ($el, attr, fallback = 0) {
        const raw = $el?.getAttribute?.(attr);
        const n = Number.parseInt(raw ?? '', 10);
        return Number.isFinite(n) ? n : fallback;
    }
}

export { Numbers };

// Auto-initializer for newly loaded content blocks
window.addEventListener('content.loaded', (e) => {
    const { $context } = e?.detail ?? {};
    if (!$context) return;

    $context.querySelectorAll('[data-numbers="root"]').forEach(($el) => {
        $el.API = ($el.API ?? new Numbers($el)).initialize();
    });
});
